import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import axios from "axios";
import "../../styles/AdmissionCard.css";
import "../../styles/DischargeModal.css";
import DoctorLogs from "./DoctorLogs";
import MedicationRequests from "./MedicationRequests";
import VitalsLogs from "./VitalsLogs";
import TreatmentPlan from "./TreatmentPlan";
import ActiveTreatmentPlan from "./ActiveTreatmentPlan";
import TreatmentPlanModal from "./TreatmentPlanModal";
import DischargeModal, { printDischargeSheet, SHOW_DISCHARGE_PRINT, STATUS_LABELS, TAKE_HOME_STATUS_LABELS } from "./DischargeModal";
import AdmissionMedicationChart from "./AdmissionMedicationChart";
import { getFormattedDateTime } from "../../assets/Utils";
import VitalsWidget from './VitalsWidget';
import GlucoseWidget from './GlucoseWidget';
import MedicationToday from './MedicationToday';
import LabTestRequestList from "../Lab/LabTestRequestList";
import ProcedureList from "../Procedures/ProcedureList";
import FHIRQuestionnaire from './Questionaire/FHIRQuestionnaire';
import FHIRQuestionnaireView from './Questionaire/FHIRQuestionnaireView';
import GlucoseLogs from "./GlucoseLogs";
import SalesView from "../SalesView";
import PatientMessaging from "../PatientMessaging";
import AskAssistantButton from "../Messages/AskAssistantButton";
import PatientReportPanel from "../Patients/PatientReportPanel";

const AdmissionCard = ({ admissionId: admissionIdProp } = {}) => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const { admission: initialAdmission } = state || {};
  const { hasPermission } = useAuth();

  const [activeTab, setActiveTab] = useState("forms");
  const [admission, setAdmission] = useState(initialAdmission);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [doctorLogs, setDoctorLogs] = useState([]);

  const [vitalsLogs, setVitalsLogs] = useState([]);

  const [activePlan, setActivePlan] = useState(null);
  const [plansLoading, setPlansLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPlan, setModalPlan] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [questionnaireStatus, setQuestionnaireStatus] = useState('not-started');
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [isResponseExpanded, setIsResponseExpanded] = useState(true);
  const [hasDiabetes, setHasDiabetes] = useState(false);
  const [diagnosis, setDiagnosis] = useState('');
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [showNewPlanConfirmation, setShowNewPlanConfirmation] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [dischargeModalOpen, setDischargeModalOpen] = useState(false);

  // Add new ref to track if component is mounted
  const isMounted = React.useRef(true);

  // Add function to fetch admission data
  const fetchCurrentAdmission = async (admissionId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/patients/admissions/${admissionId}`
      );
      if (isMounted.current) {
        setAdmission(response.data);
      }
    } catch (error) {
      console.error("Error fetching admission data:", error);
    }
  };

  useEffect(() => {
    // Check for URL parameter admissionId
    const admissionIdFromUrl = admissionIdProp || searchParams.get('admissionId');
    
    if (admissionIdFromUrl && !initialAdmission) {
      // If we have URL parameter but no state, fetch admission data
      const fetchAdmissionFromUrl = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/patients/admissions/${admissionIdFromUrl}`
          );
          if (isMounted.current) {
            setAdmission(response.data);
          }
        } catch (error) {
          console.error("Error fetching admission data from URL:", error);
          if (isMounted.current) {
            setError("Failed to load admission information. Please check the admission ID and try again.");
          }
        } finally {
          if (isMounted.current) {
            setLoading(false);
          }
        }
      };
      
      fetchAdmissionFromUrl();
    } else if (initialAdmission?.admission_id) {
      // If we have initial admission, use it and fetch fresh data
      fetchCurrentAdmission(initialAdmission.admission_id);
      refreshAdmissionData();
    }

    // Set up auto-refresh interval only if we have an admission
    const admissionId = admissionIdFromUrl || initialAdmission?.admission_id;
    if (admissionId) {
      const intervalId = setInterval(() => {
        if (isMounted.current) {
          fetchCurrentAdmission(admissionId);
          refreshAdmissionData();
        }
      }, 5 * 60 * 1000); // 5 minutes

      // Cleanup function
      return () => {
        isMounted.current = false;
        clearInterval(intervalId);
      };
    }
  }, [initialAdmission?.admission_id, searchParams, admissionIdProp]);

  // New refresh function that only updates necessary data
  const refreshAdmissionData = async () => {
    try {
      // Only fetch data that needs to be refreshed
      await Promise.all([
        fetchActivePlan(),
        fetchQuestionnaireStatus()
      ]);
    } catch (error) {
      console.error("Error refreshing admission data:", error);
    }
  };

  useEffect(() => {
    if (admission) {
      fetchActivePlan();
      fetchQuestionnaireStatus();
    }
  }, [admission]);

  // Fetch the active treatment plan
  const fetchActivePlan = async () => {
    if (!admission?.admission_id) return;
    
    setPlansLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/treatment-plans/active`,
        { params: { admission_id: admission.admission_id } }
      );
      setActivePlan(res.data || null);
    } catch (error) {
      console.error("Error fetching active treatment plan:", error);
      setActivePlan(null);
    } finally {
      setPlansLoading(false);
    }
  };

  // Stop (deactivate) the active plan
  const stopPlan = async (planId) => {
    if (!planId) return;
    try {
      await axios.put(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/treatment-plans/${planId}`,
        { is_active: false }
      );
      fetchActivePlan();
    } catch (error) {
      console.error("Error stopping plan:", error);
      alert("Failed to stop the plan.");
    }
  };

  // Add handler for new plan with confirmation
  const handleNewPlanClick = () => {
    if (activePlan) {
      setShowNewPlanConfirmation(true);
    } else {
      setModalPlan(null);
      setModalOpen(true);
    }
  };

  const confirmNewPlan = () => {
    setShowNewPlanConfirmation(false);
    setModalPlan(null);
    setModalOpen(true);
  };

  // Update fetchQuestionnaireStatus to check for diabetes
  const fetchQuestionnaireStatus = async () => {
    if (!admission?.admission_id) return;
    
    try {
      const [statusRes, dataRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/questionnaire/${admission.admission_id}/status`
        ),
        axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/questionnaire/${admission.admission_id}`
        )
      ]);
      setQuestionnaireStatus(statusRes.data.status);
      setQuestionnaireData(dataRes.data);
      console.log('Questionnaire:', dataRes.data);
      // Check if diabetes is selected in the questionnaire response
      const response = dataRes?.data?.questionnaire_response;
      if (response) {
        const pmhGroup = response.pmhChronicDiseases || [];

        setHasDiabetes(pmhGroup?.some(
          item => item === 'diabetes') || false);
        
        // Extract diagnosis from assessment plan
        const diagnosisValue = response.diagnosis || '';
        setDiagnosis(diagnosisValue);
        
        console.log('Has diabetes:', hasDiabetes);
        console.log('Diagnosis:', diagnosisValue);
      } else {
        setHasDiabetes(false);
        setDiagnosis('');
      }
    } catch (error) {
      console.error('Error fetching questionnaire data:', error);
      setQuestionnaireStatus('not-started');
      setQuestionnaireData(null);
      setHasDiabetes(false);
      setDiagnosis('');
    }
  };

  // Add Forms component
  const Forms = () => (
    <div className="forms-container">
      <h3>Available Forms</h3>
      
      {/* Only show the questionnaire button if not completed */}
      {questionnaireStatus !== 'completed' && (
        <div className="form-links">
          <button
            onClick={() => {
              setSelectedForm('patient-history');
              setFormModalOpen(true);
            }}
            className={`form-link ${questionnaireStatus}`}
          >
            Patient History Questionnaire
            <span className="form-status">
              {questionnaireStatus === 'completed' ? '(Completed)' : '(Incomplete)'}
            </span>
          </button>
          {/* Add more form links here */}
        </div>
      )}

      {/* Always show the view form if there's questionnaire data */}
      {questionnaireData && questionnaireData.questionnaire_response && (
        <div className="questionnaire-response">
          <div 
            className="response-header" 
            onClick={() => setIsResponseExpanded(!isResponseExpanded)}
          >
            <span>{isResponseExpanded ? '▼' : '▶'}</span>
          </div>
          {isResponseExpanded && (
            <FHIRQuestionnaireView
              admissionId={admission.admission_id}
              onUpdate={fetchQuestionnaireStatus}
            />
          )}
        </div>
      )}
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case "treatmentPlan":
        return <TreatmentPlan admissionId={admission.admission_id} OnChange={() => {
          fetchActivePlan(); // refresh header
          // optionally refresh list by tab change
          if (activeTab === 'treatmentPlan') setActiveTab('treatmentPlan');
        }} />;
      case "medicationChart":
        return <AdmissionMedicationChart 
          admissionId={admission.admission_id}
          disableEdit={!!admission.discharge_date}
        />;
      case "doctorLogs":
        return <DoctorLogs 
          admissionId={admission.admission_id} 
          disableEdit={!!admission.discharge_date}
        />;
      case "medicationRequests":
        return (
          <MedicationRequests
            admissionId={admission.admission_id}
            disableAdd={!!admission.discharge_date}
          />
        );
      case "vitalsLogs":
        return (
          <VitalsLogs 
            logs={vitalsLogs} 
            admissionId={admission.admission_id}
            disableEdit={!!admission.discharge_date}
          />
        );
      case "labRequests":
          return (
          <LabTestRequestList
            admissionId={admission.admission_id}
            disableEdit={!!admission.discharge_date}
            patient={admission.Patient}
          />
        );
      case "procedures":
        return (
          <ProcedureList
            admissionId={admission.admission_id}
            patient={admission.Patient}
          />
        );
      case "forms":
        return <Forms />;
      case "glucoseLogs":
        return (
          <GlucoseLogs
            admissionId={admission.admission_id}
            disableEdit={!!admission.discharge_date}
          />
        );
      default:
        return null;
    }
  };

  const tabConfig = [
    {
      id: 'forms',
      label: 'Patient Information',
      path: '/admission-card/forms'
    },
    {
      id: 'treatmentPlan',
      label: 'Treatment Plan',
      path: '/admission-card/treatment-plans'
    },
    {
      id: 'doctorLogs',
      label: 'Progress Notes',
      path: '/admission-card/doctor-logs'
    },
    {
      id: 'medicationRequests',
      label: 'Medication Requests',
      path: '/admission-card/medication-requests'
    },
    {
      id: 'vitalsLogs',
      label: 'Vitals Logs',
      path: '/admission-card/vitals-logs'
    },
    {
      id: 'medicationChart',
      label: 'Medication Chart',
      path: '/admission-medication-chart'
    },
    {
      id: 'labRequests',
      label: 'Lab Requests',
      path: '/lab-requests',
    },
    {
      id: 'procedures',
      label: 'Surgeries',
      path: '/admission-card/procedures'
    },
    hasDiabetes && {
      id: 'glucoseLogs',
      label: 'Glucose Log',
      path: '/admission-card/glucose-logs'
    }
  ].filter(Boolean);

  // Handle loading state
  if (loading) {
    return (
      <div className="admission-card" style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading admission information...</div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="admission-card" style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>
        <button onClick={() => window.history.back()}>Go Back</button>
      </div>
    );
  }

  // Handle no admission data
  if (!admission) {
    return (
      <div className="admission-card" style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading admission information...</div>
      </div>
    );
  }

  return (
    <div className="admission-card">
      {/* Modal for add/edit plan */}
      <TreatmentPlanModal
        admissionId={admission.admission_id}
        plan={modalPlan}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={() => {
          fetchActivePlan(); // refresh header
          // optionally refresh list by tab change
          const currentTab = activeTab;
          setActiveTab(null);
          setActiveTab(currentTab);
        }}
      />

      <DischargeModal
        admission={admission}
        discharge={admission.discharge}
        diagnosis={diagnosis}
        isOpen={dischargeModalOpen}
        onClose={() => setDischargeModalOpen(false)}
        onSaved={(result) => {
          if (result?.admission) {
            setAdmission((prev) => ({ ...prev, ...result.admission }));
          } else {
            fetchCurrentAdmission(admission.admission_id);
          }
        }}
      />

      {/* Forms Modal */}
      {formModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <button 
              className="modal-close" 
              onClick={() => {
                setFormModalOpen(false);
                fetchQuestionnaireStatus(); // Refresh status when modal is closed
              }}
            >
              ×
            </button>
            {selectedForm === 'patient-history' && (
              <FHIRQuestionnaire 
                admissionId={admission.admission_id} 
                onClose={() => {
                  setFormModalOpen(false);
                  fetchQuestionnaireStatus(); // Refresh status when questionnaire is completed
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Sales Modal */}
      {salesModalOpen && admission.sales_number && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <button 
              className="modal-close" 
              onClick={() => setSalesModalOpen(false)}
            >
              ×
            </button>
            <SalesView salesNumber={admission.sales_number} />
          </div>
        </div>
      )}

      {/* New Plan Confirmation Modal */}
      {showNewPlanConfirmation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Create New Treatment Plan</h3>
            <p>
              Creating a new treatment plan will invalidate any previous medication orders assigned. 
              Are you sure you want to create a new plan and remove all previous medications?
            </p>
            <div className="modal-actions">
              <button 
                onClick={confirmNewPlan}
                className="btn-primary"
              >
                Yes, Create New Plan
              </button>
              <button 
                onClick={() => setShowNewPlanConfirmation(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="admission-card-header">
        <div className="admission-card-header-section">
          <p>
            <strong>Admission ID:</strong> {admission.admission_id}
            <PatientMessaging 
              patient={admission.Patient} 
              admission={admission}
              placement="admission-card"
            />
            <AskAssistantButton
              patient={admission.Patient}
              admission={admission}
              placement="admission-card"
            />
            {hasPermission('/ai-assist/patient-report', 'Execute') && (
              <button
                type="button"
                onClick={() => setShowReport(true)}
                className="btn-patient-report"
                title="Generate a patient report"
              >
                Patient Report
              </button>
            )}
          </p>
          <div className="patient-info-box">
            <p className="patient-name">
              <strong>Name:</strong> {admission.Patient.patient_first_name}{" "}
              {admission.Patient.patient_last_name}
            </p>
            <p className="patient-number">
              <strong>Patient Number:</strong>
              <Link to="/patient-card" state={{ patient: admission.Patient }}>
                {admission.Patient.patient_number}
              </Link>
            </p>
            <p>
              <strong>Patient DOB:</strong> {admission.Patient.patient_dob}
            </p>
            <p>
              <strong>Patient Sex:</strong> {admission.Patient.patient_sex}
            </p>
            <p>
              <strong>Patient Type:</strong> {admission.Patient.patient_type} - { admission.Patient.patient_type === "Company" ? admission.Patient.company : ""}
            </p>
          </div>
          <p>
            <strong>Branch:</strong> {admission.branch}
          </p>
          <p>
            <strong>Ward:</strong> {admission.ward}
          </p>
          <p>
            <strong>Admission Date:</strong>{" "}
            {getFormattedDateTime(admission.admission_date)}
          </p>
          <p>
            <strong>Discharge Date:</strong>{" "}
            {admission.discharge_date
              ? getFormattedDateTime(admission.discharge_date)
              : ""}
          </p>
          <p>
            <strong>Status:</strong>{" "}
            <span className={admission.clinical_status === 'ready_for_discharge' ? 'discharge-status-ready' : ''}>
              {STATUS_LABELS[admission.clinical_status] || STATUS_LABELS.admitted}
            </span>
          </p>
          <p>
            <strong>Invoice Number:</strong> 
            {admission.sales_number ? (
              hasPermission('/patient-card/Tabs/Billing', 'View') ? (
                <span 
                  style={{ 
                    color: '#007bff', 
                    cursor: 'pointer', 
                    textDecoration: 'underline',
                    marginLeft: '5px'
                  }}
                  onClick={() => setSalesModalOpen(true)}
                >
                  {admission.sales_number}
                </span>
              ) : (
                <span style={{ marginLeft: '5px' }}>{admission.sales_number}</span>
              )
            ) : (
              <span style={{ marginLeft: '5px' }}>Not assigned</span>
            )}
          </p>

          {/* Consent Form Alert */}
          {(() => {
            const hasConsent = !!admission.consentFile;
            const isICU = admission.ward && admission.ward.toUpperCase().includes('ICU');
            const hasICUConsent = !!admission.icuConsentFile;
            const consentComplete = hasConsent && (isICU ? hasICUConsent : true);
            if (consentComplete) return null;
            const missing = [];
            if (!hasConsent) missing.push('General Consent Form');
            if (isICU && !hasICUConsent) missing.push('ICU Consent Form');
            return (
              <div className="consent-alert-card">
                <span className="consent-alert-card-icon">&#9888;</span>
                <div>
                  <strong>Consent Missing</strong>
                  <span className="consent-alert-card-detail">
                    {missing.join(' & ')} not uploaded
                  </span>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="admission-card-header-section widget-section">
          <VitalsWidget admissionId={admission.admission_id} />
          {hasDiabetes && <GlucoseWidget admissionId={admission.admission_id} />}
          <MedicationToday admissionId={admission.admission_id} />
        </div>

        <div className="admission-card-header-section">
          {/* Diagnosis Section */}
          <div className="diagnosis-section">
            <h4>Diagnosis</h4>
            {diagnosis ? (
              <div className="diagnosis-content-prominent">
                <p>{diagnosis}</p>
              </div>
            ) : (
              <div className="diagnosis-placeholder">
                No diagnosis available - Complete patient questionnaire to view diagnosis
              </div>
            )}
          </div>

          <h4 className="treatment-plan-title">Active Treatment Plan</h4>
          
          {plansLoading ? (
            <div>Loading active plan...</div>
          ) : activePlan ? (
            <ActiveTreatmentPlan 
              plan={activePlan} 
              discharge_date={admission.discharge_date}
            />
          ) : (
            <div style={{ color: 'red' }}>
              No active treatment plan found..admission_id} />
            </div>
          )}

          {hasPermission('/admission-card/treatment-plans', 'Edit') && (
            <div className="active-plan-actions">
              <button
                onClick={() => {
                  setModalPlan(activePlan);
                  setModalOpen(true);
                }}
                disabled={!activePlan || admission.discharge_date}
              >
                Edit Plan
              </button>
              <button
                onClick={() => stopPlan(activePlan?.plan_id)}
                className="danger"
                disabled={!activePlan || admission.discharge_date}
              >
                Stop Active Plan
              </button>
              <button
                onClick={handleNewPlanClick}
                disabled={admission.discharge_date}
              >
                New Plan
              </button>
            </div>
          )}
          {(hasPermission('/admission-card/discharge', 'Add') || hasPermission('/admission-card/discharge', 'Edit') || hasPermission('/admission-card/discharge', 'View')) && (
            <div className="active-plan-actions">
              {hasPermission('/admission-card/discharge', 'Add') && !admission.discharge_date && admission.clinical_status !== 'ready_for_discharge' && (
                <button
                  className="discharge-patient-btn"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setDischargeModalOpen(true)}
                >
                  Discharge Patient
                </button>
              )}
              {(hasPermission('/admission-card/discharge', 'Add') || hasPermission('/admission-card/discharge', 'Edit') || hasPermission('/admission-card/discharge', 'View')) && admission.clinical_status === 'ready_for_discharge' && (
                <button
                  className="discharge-patient-btn"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setDischargeModalOpen(true)}
                >
                  {admission.discharge_date || !(hasPermission('/admission-card/discharge', 'Edit') || hasPermission('/admission-card/discharge', 'Add')) ? 'View Discharge' : 'Update Discharge'}
                </button>
              )}
              {admission.discharge && admission.clinical_status === 'discharged' && hasPermission('/admission-card/discharge', 'View') && (
                <button
                  className="discharge-patient-btn"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setDischargeModalOpen(true)}
                >
                  View Discharge
                </button>
              )}
            </div>
          )}
        </div>

      </header>

      {(admission.clinical_status === 'ready_for_discharge' || (admission.clinical_status === 'discharged' && admission.discharge)) && (
        <div className={`discharge-banner ${admission.clinical_status === 'discharged' ? 'discharged' : ''}`}>
          <div>
            {admission.clinical_status === 'ready_for_discharge' ? (
              <>
                <strong>Ready for Discharge</strong>
                <span> — waiting for pharmacy and admin. Take-home medicines: {TAKE_HOME_STATUS_LABELS[admission.take_home_status] || 'None'}.</span>
              </>
            ) : (
              <>
                <strong>Discharged</strong>
                {admission.discharge_date ? ` on ${getFormattedDateTime(admission.discharge_date)}` : ''}.
              </>
            )}
          </div>
          <div className="discharge-banner-actions">
            {SHOW_DISCHARGE_PRINT && admission.discharge && (
              <button
                type="button"
                className="secondary"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => printDischargeSheet({ admission, discharge: admission.discharge })}
              >
                Print
              </button>
            )}
            {(hasPermission('/admission-card/discharge', 'View') || hasPermission('/admission-card/discharge', 'Add') || hasPermission('/admission-card/discharge', 'Edit')) && (
              <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => setDischargeModalOpen(true)}>
                {admission.clinical_status === 'ready_for_discharge' && !admission.discharge_date ? 'Review' : 'View'}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="admission-card-details">
        <nav className="admission-card-tabs">
          <ul>
            {tabConfig.map((tab) => (
              hasPermission(tab.path, 'View') && (
                <li
                  key={tab.id}
                  className={activeTab === tab.id ? 'active' : ''}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </li>
              )
            ))}
          </ul>
        </nav>
        <div className="admission-card-content">
          {hasPermission(tabConfig.find(tab => tab.id === activeTab)?.path, 'View') && renderActiveTab()}
        </div>
      </div>

      {admission.Patient && hasPermission('/ai-assist/patient-report', 'Execute') && (
        <PatientReportPanel
          visible={showReport}
          onClose={() => setShowReport(false)}
          source="admission"
          admissionId={admission.admission_id}
          patientId={admission.Patient.patient_id}
        />
      )}
    </div>
  );
};

export default AdmissionCard;
